import React, { useState } from "react";
import BackButton from "../components/BackButton"; // Ensure this path is correct

function HowToPlay() {
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? -1 : index);
  };

  const steps = [
    {
      title: "What is Fantasy Cricket?",
      icon: "controller",
      color: "#4e73df", // Blue
      content: (
        <>
          <p className="mb-2">
            Fantasy Cricket is a strategy-based online sports game where you create a virtual team of real cricketers playing in real-world matches.
          </p>
          <ul className="mb-0 ps-3 small text-secondary">
            <li>Your team earns points based on real-life performance (Runs, Wickets, Catches).</li>
            <li>Compete with other users and win real cash rewards.</li>
            <li>It's a game of skill, knowledge, and analysis.</li>
          </ul>
        </>
      ),
    },
    {
      title: "Select a Match",
      icon: "calendar-event",
      color: "#1cc88a", // Green
      content: (
        <>
          <p className="mb-2">
            Browse the "Upcoming Matches" list on the home screen. Matches are categorized by tournaments (IPL, World Cup, etc.).
          </p>
          <div className="alert alert-light border small mb-0">
            <strong><i className="bi bi-info-circle me-1"></i> Note:</strong> Always check the match deadline. Teams get locked once the match starts.
          </div>
        </>
      ),
    },
    {
      title: "Create Your Team",
      icon: "person-plus-fill",
      color: "#36b9cc", // Cyan
      content: (
        <>
          <p className="mb-2">
            You have a budget of <strong>100 Credits</strong> to select your best 11 players.
          </p>
          <h6 className="fw-bold small text-dark mt-2">Team Combination Rules:</h6>
          <ul className="mb-0 ps-3 small text-secondary">
            <li><strong>WK:</strong> 1-4 Wicket Keepers</li>
            <li><strong>BAT:</strong> 3-6 Batsmen</li>
            <li><strong>AR:</strong> 1-4 All-Rounders</li>
            <li><strong>BOWL:</strong> 3-6 Bowlers</li>
            <li>Max <strong>7 players</strong> from a single real-life team.</li>
          </ul>
        </>
      ),
    },
    {
      title: "Choose Captain & VC",
      icon: "star-fill",
      color: "#f6c23e", // Yellow
      content: (
        <>
          <p className="mb-2">
            This is the most critical step to winning! These two players give you bonus points.
          </p>
          <div className="d-flex gap-2 mt-2">
            <div className="bg-light border rounded p-2 flex-grow-1 text-center">
              <span className="d-block fw-bold text-danger">Captain (C)</span>
              <small>Gets <strong>2x</strong> Points</small>
            </div>
            <div className="bg-light border rounded p-2 flex-grow-1 text-center">
              <span className="d-block fw-bold text-primary">Vice-Captain (VC)</span>
              <small>Gets <strong>1.5x</strong> Points</small>
            </div>
          </div>
        </>
      ),
    },
    {
      title: "Join Contests",
      icon: "trophy-fill",
      color: "#e74a3b", // Red
      content: (
        <>
          <p className="mb-2">
            Once your team is ready, join a contest to compete.
          </p>
          <ul className="mb-0 ps-3 small text-secondary">
            <li><strong>Mega Contest:</strong> Big prize pool, low entry fee.</li>
            <li><strong>Head-to-Head:</strong> 1 vs 1 battle. Higher chance of winning.</li>
            <li><strong>Practice Contest:</strong> Free entry to improve skills.</li>
          </ul>
        </>
      ),
    },
  ];

  return (
    <div
      className="how-to-play-container"
      style={{ minHeight: "100vh", background: "#f3f4f6", paddingBottom: "100px" }}
    >
      {/* === HERO HEADER IN MAROON THEME === */}
      <div 
        className="pb-5 pt-4 px-3 position-relative overflow-hidden shadow-sm"
        style={{ 
            background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", // 🔥 Maroon Gradient
            borderBottomLeftRadius: "30px", 
            borderBottomRightRadius: "30px",
            height: "180px",
            boxShadow: "0 10px 20px rgba(128,0,0,0.15)"
        }}
      >
         <div className="position-relative z-1">
             <BackButton className="text-white bg-transparent border-0" />
             <div className="mt-2 px-2">
                <h3 className="text-white fw-bold mb-1">How to Play</h3>
                <p className="text-white opacity-75 small">Master the game in 5 simple steps</p>
             </div>
         </div>
      </div>

      <div className="container-fluid px-3 px-md-4" style={{ marginTop: "-40px" }}>
        
        {/* === STEPS ACCORDION === */}
        <div className="d-flex mt-4 flex-column gap-3">
          {steps.map((step, idx) => {
            const isActive = activeIndex === idx;
            return (
              <div
                key={idx}
                className="card border-0 shadow-sm"
                style={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  transform: isActive ? "scale(1.02)" : "scale(1)",
                  border: isActive ? `1px solid ${step.color}` : "1px solid transparent",
                  boxShadow: isActive ? "0 10px 20px rgba(0,0,0,0.08)" : "0 2px 5px rgba(0,0,0,0.05)"
                }}
              >
                {/* Header */}
                <div
                  className="card-header bg-white border-0 p-3 d-flex align-items-center justify-content-between"
                  onClick={() => toggleAccordion(idx)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-3">
                    {/* Icon Box */}
                    <div
                      className="d-flex align-items-center justify-content-center text-white shadow-sm"
                      style={{
                        width: "45px",
                        height: "45px",
                        borderRadius: "12px",
                        background: `linear-gradient(135deg, ${step.color}, ${step.color}dd)`,
                        fontSize: "1.2rem",
                      }}
                    >
                      <i className={`bi bi-${step.icon}`}></i>
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0 text-dark" style={{fontSize: "0.95rem"}}>{step.title}</h6>
                      <small className="text-muted" style={{fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px"}}>Step {idx + 1}</small>
                    </div>
                  </div>

                  {/* Arrow */}
                  <i
                    className={`bi bi-chevron-down text-muted`}
                    style={{
                      transition: "transform 0.3s ease",
                      transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  ></i>
                </div>

                {/* Body Content */}
                <div
                  style={{
                    maxHeight: isActive ? "300px" : "0px",
                    overflow: "hidden",
                    transition: "max-height 0.4s ease-in-out",
                    background: "#fff",
                  }}
                >
                  <div className="p-3 pt-0 text-secondary border-top" style={{ fontSize: "0.9rem", lineHeight: "1.6" }}>
                    <div className="mt-3">
                        {step.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* === SCORING MULTIPLIERS === */}
        <div className="mt-5">
          <h6 className="fw-bold mb-3 d-flex align-items-center text-uppercase text-muted small ls-1">
            <i className="bi bi-lightning-charge-fill text-warning me-2"></i>
            Key Multipliers
          </h6>
          
          <div className="row g-3">
            {/* Captain */}
            <div className="col-6">
              <div className="p-3 rounded-4 text-center position-relative overflow-hidden border-0 shadow-sm" 
                   style={{ background: "white" }}>
                <div className="position-absolute top-0 start-0 w-100 h-100 bg-danger opacity-10"></div>
                <div className="position-relative z-1">
                    <div className="badge bg-danger fs-6 mb-2 px-3 py-1 rounded-pill shadow-sm">Captain</div>
                    <h2 className="fw-bold mb-0 display-6" style={{ color: "#800000" }}>2x</h2>
                    <small className="text-muted fw-bold" style={{fontSize: '0.7rem'}}>POINTS</small>
                </div>
              </div>
            </div>

            {/* Vice Captain */}
            <div className="col-6">
              <div className="p-3 rounded-4 text-center position-relative overflow-hidden border-0 shadow-sm" 
                   style={{ background: "white" }}>
                <div className="position-absolute top-0 start-0 w-100 h-100 bg-primary opacity-10"></div>
                <div className="position-relative z-1">
                    <div className="badge fs-6 bg-primary text-white mb-2 px-3 py-1 rounded-pill shadow-sm">Vice Captain</div>
                    <h2 className="fw-bold mb-0 display-6" style={{ color: "#A00000" }}>1.5x</h2>
                    <small className="text-muted fw-bold" style={{fontSize: '0.7rem'}}>POINTS</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === PRO TIPS IN MAROON THEME === */}
        <div className="card mt-4 border-0 shadow text-white position-relative overflow-hidden" 
             style={{ borderRadius: "20px", background: "linear-gradient(135deg, #A00000 0%, #6A0000 100%)" }}>
          
          {/* Gold Shine Effect */}
          <div className="position-absolute top-0 end-0 bg-warning opacity-25" style={{ width: '150px', height: '150px', filter: 'blur(60px)', borderRadius: '50%' }}></div>

          <div className="card-body p-4 position-relative z-1">
            <h5 className="fw-bold mb-3 text-warning">
              Pro Tips to Win
            </h5>
            <ul className="list-unstyled mb-0">
              {[
                "Always check the Pitch Report.",
                "Pick top-order batsmen in T20s.",
                "All-rounders are safe Captain choices.",
                "Wait for the Toss to finalize team."
              ].map((tip, i) => (
                <li key={i} className="d-flex align-items-center gap-3 mb-3 last-mb-0">
                  <div className="rounded-circle bg-white text-danger d-flex align-items-center justify-content-center flex-shrink-0 shadow-sm" style={{width:'24px', height:'24px'}}>
                      <i className="bi bi-check-lg small fw-bold"></i>
                  </div>
                  <span style={{ fontSize: "0.9rem", opacity: 0.9 }}>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

      </div>
      <style>{` .ls-1 { letter-spacing: 1px; } `}</style>
    </div>
  );
}

export default HowToPlay;